import React, { ReactNode } from "react";
import styled from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";
import type { WebTheme } from "@ifgengineering/design-tokens/legacy";

export type V3BadgeTone =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "premium"
  | "successSubtle"
  | "errorSubtle"
  | "accent"
  | "neutralSubtle"
  | "isa"
  | "deadline"
  | "failed";

export type V3BadgeSize = "sm" | "md";

export interface V3BadgeProps {
  children: ReactNode;
  tone: V3BadgeTone;
  size?: V3BadgeSize;
  className?: string;
  testID?: string;
}

interface StyledBadgeProps {
  $tone: V3BadgeTone;
  $size: V3BadgeSize;
}

interface BadgeColours {
  background: string;
  text: string;
}

type PaletteTone = Exclude<V3BadgeTone, "isa" | "deadline" | "failed">;

/** Light keeps the classic palette; these two read as an existing light tone. */
const LIGHT_TONE: Record<V3BadgeTone, PaletteTone> = {
  success: "success",
  warning: "warning",
  error: "error",
  info: "info",
  neutral: "neutral",
  premium: "premium",
  successSubtle: "successSubtle",
  errorSubtle: "errorSubtle",
  accent: "accent",
  neutralSubtle: "neutralSubtle",
  isa: "successSubtle",
  deadline: "accent",
  failed: "error",
};

/** Figma DS 2026 pill-badge styles, dark palette only. */
const DARK_PILL: Partial<
  Record<V3BadgeTone, (colors: WebTheme["colors"]) => BadgeColours>
> = {
  isa: (c) => ({
    background: c.domainSpecific.entityTypes.chipIsa,
    text: c.ui.text.primary,
  }),
  accent: (c) => ({
    background: c.ui.background.info,
    text: c.ui.text.primary,
  }),
  neutral: (c) => ({
    background: c.domainSpecific.entityTypes.chipNeutral,
    text: c.ui.text.secondary,
  }),
  neutralSubtle: (c) => ({
    background: c.domainSpecific.entityTypes.chipNeutral,
    text: c.ui.text.secondary,
  }),
  warning: (c) => ({
    background: c.ui.background.warning,
    text: c.ui.text.warning,
  }),
  deadline: (c) => ({
    background: c.ui.background.warning,
    text: c.ui.text.warning,
  }),
  failed: (c) => ({
    background: c.ui.background.error,
    text: c.ui.text.primary,
  }),
};

const badgeColours = (theme: WebTheme, tone: V3BadgeTone): BadgeColours => {
  const dark = theme.mode === "dark" ? DARK_PILL[tone] : undefined;
  if (dark) return dark(theme.colors);
  return theme.colors.domainSpecific.badgeTypes[LIGHT_TONE[tone]];
};

const StyledBadge = styled.span<StyledBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space["3xs"]}px;
  white-space: nowrap;
  border-radius: ${({ theme }) => theme.radius.full}px;
  background: ${({ theme, $tone }) => badgeColours(theme, $tone).background};
  color: ${({ theme, $tone }) => badgeColours(theme, $tone).text};
  padding: ${({ theme }) => theme.space["2xs"]}px
    ${({ theme, $size }) =>
      $size === "sm" ? theme.space.sm : theme.space.md}px;

  ${({ $size }) =>
    $size === "sm"
      ? typographyCSS("caption.sm-medium") +
        "font-size: 11px; line-height: 14px;"
      : typographyCSS("caption.sm-medium") + "font-weight: 600;"}
`;

export const V3Badge = ({
  children,
  tone,
  size = "md",
  className,
  testID,
}: V3BadgeProps): JSX.Element => (
  <StyledBadge
    $tone={tone}
    $size={size}
    className={className}
    data-testid={testID}
  >
    {children}
  </StyledBadge>
);
